import React, { useState, useCallback } from 'react';
import { Nav, Modal, Button } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DICT } from '../utils/dictionary';

function NavIcon({ paths }) {
  return (
    <svg className="nav-icon" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      {paths.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}

const IC = {
  dashboard:  ['M3 3h7v7H3z', 'M14 3h7v7h-7z', 'M14 14h7v7h-7z', 'M3 14h7v7H3z'],
  producao:   ['M15 10l4.55-2.7A1 1 0 0 1 21 8.2v7.6a1 1 0 0 1-1.45.9L15 14', 'M3 8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'],
  equipe:     ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
  pagamentos: ['M12 1v22', 'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'],
  regras:     ['M4 21v-7', 'M4 10V3', 'M12 21v-9', 'M12 8V3', 'M20 21v-5', 'M20 12V3', 'M1 14h6', 'M9 8h6', 'M17 16h6'],
  auditoria:  ['M12 2l8 3v7c0 5-4 8.5-8 10C8 20.5 4 17 4 12V5l8-3z', 'M9 12l2 2 4-4'],
  metas:      ['M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z', 'M4 22v-7'],
  desempenho: ['M23 6l-9.5 9.5-5-5L1 18', 'M17 6h6v6'],
  conta:      ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
  sair:       ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 17l5-5-5-5', 'M21 12H9'],
  menu:       ['M3 12h18', 'M3 6h18', 'M3 18h18'],
  close:      ['M18 6L6 18', 'M6 6l12 12'],
};

const Sidebar = ({ toggleSidebar, sidebarOpen, collapsed }) => {
  const { logout, acesso, isDemo, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [floatTip, setFloatTip] = useState({ text: '', x: 0, y: 0, on: false });

  const showTip = useCallback((e, text) => {
    if (!collapsed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setFloatTip({ text, x: r.right + 10, y: r.top + r.height / 2, on: true });
  }, [collapsed]);
  const hideTip = useCallback(() => setFloatTip(t => ({ ...t, on: false })), []);

  const CLIENT_NAME = process.env.REACT_APP_CLIENT_NAME || 'Dashboard';
  const LOGO_MAIN    = process.env.REACT_APP_LOGO_MAIN    || '/client_assets/logo-main.png';
  const LOGO_COMPACT = process.env.REACT_APP_LOGO_COMPACT || '/client_assets/logo-icon.png';
  const LOGO_MODAL   = process.env.REACT_APP_LOGO_MODAL   || LOGO_MAIN;

  const handleLogout = () => {
    logout();
    navigate('/login');
    if (window.innerWidth <= 768) toggleSidebar();
  };
  const handleNavClick = () => {
    if (window.innerWidth <= 768) toggleSidebar();
  };
  const active = (path) => location.pathname === path ? 'active-link' : '';
  return (
    <>
      {/* Toggle + Logo — fora do nav scrollável */}
      <button className="navbar-toggle mb-1" onClick={toggleSidebar}
              aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}>
        <NavIcon paths={collapsed ? IC.menu : IC.close} />
      </button>

      <div className="sidebar-logo mb-2 text-center px-2">
        {isDemo ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-1">
            {collapsed
              ? <div className="sidebar-demo-initial">{CLIENT_NAME.charAt(0)}</div>
              : <div className="sidebar-demo-wordmark">{CLIENT_NAME}</div>
            }
          </div>
        ) : (
          <img
            className="tenant-logo img-fluid"
            src={collapsed ? LOGO_COMPACT : LOGO_MAIN}
            alt={CLIENT_NAME}
            onError={(e) => { if (e.target.src !== window.location.origin + '/favicon.ico') e.target.src = '/favicon.ico'; }}
          />
        )}
      </div>

      {/* Nav scrollável — só links e separadores */}
      <Nav className="flex-column sidebar-nav">

        {(acesso === 'admin' || acesso === 'gerente') && (<>
          {!collapsed && <div className="sidebar-section-label">VISÃO GERAL</div>}
          <Nav.Link as={Link} to="/dashboard" className={active('/dashboard')} onClick={handleNavClick} onMouseEnter={(e) => showTip(e, 'Dashboard')} onMouseLeave={hideTip}>
            <NavIcon paths={IC.dashboard} />{!collapsed && <span className="nav-text">Dashboard</span>}
          </Nav.Link>
          <Nav.Link as={Link} to="/videos" className={active('/videos')} onClick={handleNavClick} onMouseEnter={(e) => showTip(e, 'Produção')} onMouseLeave={hideTip}>
            <NavIcon paths={IC.producao} />{!collapsed && <span className="nav-text">Produção</span>}
          </Nav.Link>

          {!collapsed && <div className="sidebar-section-label">EQUIPE</div>}
          <Nav.Link as={Link} to="/equipe" className={active('/equipe')} onClick={handleNavClick} onMouseEnter={(e) => showTip(e, 'Equipe')} onMouseLeave={hideTip}>
            <NavIcon paths={IC.equipe} />{!collapsed && <span className="nav-text">Equipe</span>}
          </Nav.Link>
          <Nav.Link as={Link} to="/pagamentos" className={active('/pagamentos')} onClick={handleNavClick} onMouseEnter={(e) => showTip(e, 'Pagamentos')} onMouseLeave={hideTip}>
            <NavIcon paths={IC.pagamentos} />{!collapsed && <span className="nav-text">Pagamentos</span>}
          </Nav.Link>
          <Nav.Link as={Link} to="/configuracoes" className={active('/configuracoes')} onClick={handleNavClick} onMouseEnter={(e) => showTip(e, `Regras de ${DICT.REWARD}`)} onMouseLeave={hideTip}>
            <NavIcon paths={IC.regras} />{!collapsed && <span className="nav-text">Regras de {DICT.REWARD}</span>}
          </Nav.Link>
          {acesso === 'admin' && (
            <Nav.Link as={Link} to="/auditoria" className={active('/auditoria')} onClick={handleNavClick} onMouseEnter={(e) => showTip(e, 'Auditoria')} onMouseLeave={hideTip}>
            <NavIcon paths={IC.auditoria} />{!collapsed && <span className="nav-text">Auditoria</span>}
            </Nav.Link>
          )}
        </>)}

        {!collapsed && <div className="sidebar-section-label">PESSOAL</div>}
        <Nav.Link as={Link} to="/metas" className={active('/metas')} onClick={handleNavClick} onMouseEnter={(e) => showTip(e, 'Metas')} onMouseLeave={hideTip}>
            <NavIcon paths={IC.metas} />{!collapsed && <span className="nav-text">Metas</span>}
        </Nav.Link>
        <Nav.Link as={Link} to="/meus-bonus" className={active('/meus-bonus')} onClick={handleNavClick} onMouseEnter={(e) => showTip(e, 'Desempenho')} onMouseLeave={hideTip}>
            <NavIcon paths={IC.desempenho} />{!collapsed && <span className="nav-text">Desempenho</span>}
        </Nav.Link>
        <Nav.Link as={Link} to="/minha-conta" className={active('/minha-conta')} onClick={handleNavClick} onMouseEnter={(e) => showTip(e, 'Minha Conta')} onMouseLeave={hideTip}>
            <NavIcon paths={IC.conta} />{!collapsed && <span className="nav-text">Minha Conta</span>}
        </Nav.Link>

      </Nav>

      {/* Footer — avatar + sair, fixo no fundo */}
      <div className="sidebar-foot">
        {!collapsed && (
          <div className="user-card">
            <div className="user-meta">
              <div className="u-name">{user?.nome || 'Usuário'}</div>
              <div className="u-role">{(user?.cargo || acesso || 'Colaborador').toUpperCase()}</div>
              {isDemo && <div className="sidebar-demo-badge">MODO DEMO</div>}
            </div>
          </div>
        )}
        <Nav.Link onClick={() => setShowConfirm(true)} className="sidebar-sair"
          onMouseEnter={(e) => showTip(e, 'Sair')} onMouseLeave={hideTip}>
          <NavIcon paths={IC.sair} />{!collapsed && <span className="nav-text">Sair</span>}
        </Nav.Link>
      </div>

      {collapsed && floatTip.on && (
        <div className="nav-floating-tip" style={{ top: floatTip.y, left: floatTip.x }}>
          {floatTip.text}
        </div>
      )}

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Body className="text-center">
          <img src={LOGO_MODAL} alt={CLIENT_NAME} style={{ maxWidth: 120, height: 'auto' }} />
          <h5 className="mt-3">Deseja encerrar a sessão?</h5>
        </Modal.Body>
        <Modal.Footer className="justify-content-center border-0">
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleLogout}>Sair</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Sidebar;
